import { z } from 'zod';

import {
  patientDuplicateCandidateResponseSchema,
  resolvePatientDuplicateRequestSchema,
  type PatientDuplicateCandidateResponse,
  type ResolvePatientDuplicateRequest,
} from '@emr/shared';

import { apiRequest } from './client.ts';

/**
 * The duplicate-patient-record review queue (`BACKLOG.md` C2a), validated against `@emr/shared`'s
 * own request/response schemas — matching `api/patients.ts`'s own stated reasoning: the server's
 * real response shape, not a locally-invented approximation of it.
 */

export type { PatientDuplicateCandidateResponse, ResolvePatientDuplicateRequest };

const duplicateListResponseSchema = z.strictObject({
  duplicates: z.array(patientDuplicateCandidateResponseSchema),
});
const duplicateResponseSchema = z.strictObject({
  duplicate: patientDuplicateCandidateResponseSchema,
});

/** `GET /api/patients/duplicates` — every pending candidate pair, requiring `patient:read`. */
export async function listPatientDuplicates(
  signal?: AbortSignal,
): Promise<readonly PatientDuplicateCandidateResponse[]> {
  const response = await apiRequest(
    '/api/patients/duplicates',
    duplicateListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.duplicates;
}

/**
 * `POST /api/patients/duplicates/:candidateId/resolve` — record a staff decision, requiring
 * `patient:write`. Confirming a pair as a duplicate records the review decision only; it does not
 * itself merge any chart data — see `db/schema/clinical.ts`'s own comment on
 * `patientDuplicateCandidates` for why that is deliberately out of scope for this pass.
 */
export async function resolvePatientDuplicate(
  candidateId: string,
  input: ResolvePatientDuplicateRequest,
): Promise<PatientDuplicateCandidateResponse> {
  resolvePatientDuplicateRequestSchema.parse(input);
  const response = await apiRequest(
    `/api/patients/duplicates/${candidateId}/resolve`,
    duplicateResponseSchema,
    { method: 'POST', body: input },
  );
  return response.duplicate;
}
